import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Breadcrumb from '../components/Breadcrumb';
import ConditionalRequirementsPanel from '../components/ConditionalRequirementsPanel';
import { 
  ArrowLeft,
  User,
  Phone,
  Mail,
  MapPin,
  Truck,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Copy,
  Link as LinkIcon,
  MessageSquare,
  Save,
  RefreshCw,
  Check,
  X,
  ExternalLink,
  ShieldAlert,
  ThumbsUp,
  ThumbsDown,
  FileQuestion,
  AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUSES = [
  { value: 'new_application', label: 'New Application' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'approved_pending_docs', label: 'Approved – Pending Docs' },
  { value: 'docs_submitted', label: 'Docs Submitted' },
  { value: 'docs_verified', label: 'Docs Verified' },
  { value: 'contract_sent', label: 'Contract Sent' },
  { value: 'contract_executed', label: 'Contract Executed' },
  { value: 'active_dispatch_eligible', label: 'Active – Dispatch Eligible' },
  { value: 'rejected', label: 'Rejected' }
];

// Final statuses that don't need quick actions
const FINAL_STATUSES = ['active_dispatch_eligible', 'rejected'];

// Approval statuses - only super_admin can set these
const APPROVAL_STATUSES = ['approved_pending_docs', 'docs_verified', 'active_dispatch_eligible'];
const REJECTION_STATUSES = ['rejected'];

const OnboardingDetails = () => {
  const { id } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageTemplate, setMessageTemplate] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [generatingToken, setGeneratingToken] = useState(false);
  const [showEscalateModal, setShowEscalateModal] = useState(false);
  const [escalationReason, setEscalationReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [auditEvents, setAuditEvents] = useState([]);

  const allowedRoles = ['admin', 'super_admin', 'dispatcher', 'safety_compliance_admin', 'ops_admin'];
  
  // Role checks
  const isSuperAdmin = user?.role === 'super_admin';
  const isOpsAdmin = user?.role === 'admin' || user?.role === 'operations_admin' || 
                     user?.role === 'safety_compliance_admin' || user?.role === 'payroll_admin' ||
                     user?.role === 'ops_admin';
  const canApproveReject = isSuperAdmin; // Only super_admin can approve/reject
  const canEscalate = isOpsAdmin && !isSuperAdmin; // Only ops_admin can escalate

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Fetch audit events for inline snippet
  const fetchAuditEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/audit-snippet?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditEvents(data.audit_events || []);
      }
    } catch (err) {
      console.error('Failed to fetch audit events:', err);
    }
  };

  const fetchApplication = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch application');
      
      const data = await response.json();
      setApplication(data);
      
      // Also fetch audit events
      fetchAuditEvents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && id) {
      fetchApplication();
    }
  }, [token, id]);

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update status');
      }
      
      await fetchApplication();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Dual Approval: Partner Approval
  const handlePartnerApproval = async (action) => {
    if (!isOpsAdmin) {
      setError('Only Partner (ops_admin) can record Partner approval');
      return;
    }
    
    if (action === 'reject' && !rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/partner-approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action, 
          notes: action === 'reject' ? rejectReason : null 
        })
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || `Failed to record Partner ${action}`);
      }
      
      setShowRejectModal(false);
      setRejectReason('');
      await fetchApplication();
      alert(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Dual Approval: Super Admin Final Approval
  const handleSuperAdminApproval = async (action) => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can record final approval');
      return;
    }
    
    if (action === 'reject' && !rejectReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/super-admin-approval`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action, 
          notes: action === 'reject' ? rejectReason : null 
        })
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || `Failed to record Super Admin ${action}`);
      }
      
      setShowRejectModal(false);
      setRejectReason('');
      await fetchApplication();
      alert(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Request Documents (resets approval chain)
  const handleRequestDocs = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/request-documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to request documents');
      }
      
      await fetchApplication();
      alert(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Reset Approvals (Super Admin only)
  const handleResetApprovals = async () => {
    if (!isSuperAdmin) {
      setError('Only Super Admin can reset approvals');
      return;
    }
    
    if (!resetReason.trim() || resetReason.length < 5) {
      setError('Please provide a reason for resetting approvals (min 5 characters)');
      return;
    }
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/reset-approvals`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: resetReason })
      });
      
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset approvals');
      }
      
      setShowResetModal(false);
      setResetReason('');
      await fetchApplication();
      alert(data.message);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Quick Action: Escalate to Super Admin
  const handleEscalate = async () => {
    if (!canEscalate) {
      setError('Only ops_admin can escalate applications');
      return;
    }
    
    if (!escalationReason.trim() || escalationReason.length < 10) {
      setError('Please provide a detailed escalation reason (min 10 characters)');
      return;
    }
    
    setEscalating(true);
    try {
      // First add note about escalation
      await fetch(`${API_URL}/api/onboarding/application/${id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          note: `[ESCALATED TO SUPER ADMIN] ${escalationReason}` 
        })
      });
      
      // Update status to indicate escalation (using reviewed as interim)
      await fetch(`${API_URL}/api/onboarding/application/${id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: 'reviewed',
          notes: `Escalated to Super Admin: ${escalationReason}`
        })
      });
      
      setShowEscalateModal(false);
      setEscalationReason('');
      await fetchApplication();
      alert('Application escalated to Super Admin successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setEscalating(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ note: newNote })
      });
      
      if (!response.ok) throw new Error('Failed to add note');
      
      setNewNote('');
      await fetchApplication();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateDocumentChecklist = async (field, value) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/documents`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ [field]: value })
      });
      
      if (!response.ok) throw new Error('Failed to update checklist');
      
      await fetchApplication();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const generateToken = async () => {
    setGeneratingToken(true);
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/generate-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to generate token');
      
      await fetchApplication();
      setCopySuccess('Token generated!');
      setTimeout(() => setCopySuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setGeneratingToken(false);
    }
  };

  const getMessageTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/onboarding/application/${id}/message-template`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to get template');
      }
      
      const data = await response.json();
      setMessageTemplate(data.message);
      setShowMessageModal(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      setCopySuccess('Failed to copy');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDriverType = (type) => {
    const types = {
      'lease_on': 'Lease-On',
      'owner_operator': 'Owner-Operator',
      'dispatch_only': 'Dispatch-Only',
      'company_driver': 'Company Driver'
    };
    return types[type] || type;
  };

  const formatLane = (lane) => {
    const lanes = { 'otr': 'OTR', 'regional': 'Regional', 'dedicated': 'Dedicated', 'any': 'Any' };
    return lanes[lane] || lane;
  };

  const formatExperience = (exp) => {
    const experiences = {
      'less_than_1': 'Less than 1 year',
      '1_2': '1-2 years',
      '2_5': '2-5 years',
      '5_10': '5-10 years',
      '10_plus': '10+ years'
    };
    return experiences[exp] || exp;
  };

  if (!user || !allowedRoles.includes(user.role)) return null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  if (!application) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">Application not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const isOwnerOperator = application.driver_type === 'owner_operator' || application.driver_type === 'dispatch_only';
  const checklist = application.document_checklist || {};

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="onboarding-details-page">
        {/* Breadcrumb Navigation */}
        <Breadcrumb 
          items={[
            { label: 'Onboarding', path: '/onboarding' },
            { label: application.full_name || 'Driver Details' }
          ]}
        />

        <ConditionalRequirementsPanel applicationId={id} token={token} />

        {/* Header */}
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {application.first_name} {application.last_name}
              </h1>
              <p className="text-sm text-gray-500 mt-1">Application ID: {application.id}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate(`/admin/person/${id}`)}
                data-testid="open-person-file-btn"
                className="px-4 py-2 bg-indigo-50 text-indigo-800 rounded-lg hover:bg-indigo-100 flex items-center gap-2"
              >
                <User className="w-4 h-4" />
                Open Person File
              </button>
              <button
                onClick={generateToken}
                disabled={generatingToken}
                data-testid="generate-token-btn"
                className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2"
              >
                <LinkIcon className="w-4 h-4" />
                {generatingToken ? 'Generating...' : 'Generate Onboarding Link'}
              </button>
              <button
                onClick={getMessageTemplate}
                disabled={!application.onboarding_token}
                data-testid="copy-message-btn"
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
              >
                <MessageSquare className="w-4 h-4" />
                Copy Message Template
              </button>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500" />
            <p className="text-red-700">{error}</p>
            <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Copy Success */}
        {copySuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <p className="text-green-700">{copySuccess}</p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Application Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Application Summary</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Full Name</p>
                    <p className="font-medium text-gray-900">{application.first_name} {application.last_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900">{application.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium text-gray-900">{application.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium text-gray-900">{application.city || '-'}, {application.state || '-'}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Truck className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Driver Type</p>
                    <p className="font-medium text-gray-900">{formatDriverType(application.driver_type)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Preferred Lane</p>
                    <p className="font-medium text-gray-900">{formatLane(application.preferred_lane)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Experience</p>
                    <p className="font-medium text-gray-900">{formatExperience(application.years_experience)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-500">Applied</p>
                    <p className="font-medium text-gray-900">{formatDate(application.created_at)}</p>
                  </div>
                </div>
              </div>
              {application.additional_notes && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">Applicant Notes</p>
                  <p className="text-gray-700">{application.additional_notes}</p>
                </div>
              )}
            </div>

            {/* Document Receipt Tracking — NOT a requirement authority.
                Authoritative requirements are shown by ConditionalRequirementsPanel above.
                These checkboxes only record which files a recruiter has received. */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-1">Document Receipt Tracking</h2>
              <p className="text-xs text-gray-500 mb-4">
                Recruiter tracking of received files only. See "Conditional Document Requirements" above
                for what actually applies to this applicant. Marking received does not mean verified.
              </p>
              <div className="space-y-3">
                {[
                  { key: 'cdl_uploaded', label: 'CDL' },
                  { key: 'medical_card_uploaded', label: 'Medical Card' },
                  { key: 'w9_uploaded', label: 'W-9' },
                  { key: 'insurance_uploaded', label: 'Insurance / COI' },
                  { key: 'registration_uploaded', label: 'Vehicle Registration' },
                  { key: 'other_docs_uploaded', label: 'Other Documents' }
                ].map(doc => (
                  <label
                    key={doc.key}
                    data-testid={`receipt-${doc.key}`}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      checklist[doc.key] ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checklist[doc.key] || false}
                      onChange={(e) => updateDocumentChecklist(doc.key, e.target.checked)}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className={`flex-1 ${checklist[doc.key] ? 'text-green-800' : 'text-gray-700'}`}>
                      {doc.label}
                    </span>
                    {checklist[doc.key] && (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <CheckCircle className="w-4 h-4 text-green-500" /> Received
                      </span>
                    )}
                  </label>
                ))}
              </div>

              {/* Contract Status */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <label className="block text-sm font-medium text-gray-700 mb-2">Contract Status</label>
                <select
                  value={checklist.contract_status || 'not_sent'}
                  onChange={(e) => updateDocumentChecklist('contract_status', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="not_sent">Not Sent</option>
                  <option value="sent">Sent</option>
                  <option value="executed">Executed</option>
                </select>
              </div>
            </div>

            {/* Internal Notes */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Internal Notes</h2>
              
              {/* Add Note */}
              <div className="mb-4">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={3}
                  data-testid="note-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <button
                  onClick={addNote}
                  disabled={saving || !newNote.trim()}
                  data-testid="add-note-btn"
                  className="mt-2 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Add Note'}
                </button>
              </div>

              {/* Notes List */}
              <div className="space-y-3">
                {(application.internal_notes || []).length === 0 ? (
                  <p className="text-gray-500 text-sm">No notes yet</p>
                ) : (
                  [...(application.internal_notes || [])].reverse().map((note, index) => (
                    <div key={index} className={`p-3 rounded-lg border ${
                      note.note?.includes('[ESCALATED') ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100'
                    }`}>
                      <p className="text-gray-700">{note.note}</p>
                      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                        <span>{note.author}</span>
                        <span>•</span>
                        <span>{formatDate(note.timestamp)}</span>
                        {note.type === 'status_change' && (
                          <>
                            <span>•</span>
                            <span className="text-blue-600">Status changed</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Sticky Rejection Banner */}
            {application.final_approval_status === 'rejected' && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="rejection-banner">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-red-800 font-semibold">Application Rejected</h3>
                    <p className="text-red-600 text-sm">
                      Reversal requires Super Admin reset.
                    </p>
                    {isSuperAdmin && (
                      <button
                        onClick={() => setShowResetModal(true)}
                        className="mt-2 text-sm text-red-700 underline hover:text-red-900"
                        data-testid="reset-approvals-link"
                      >
                        Reset Approvals to reconsider
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Dual Approval Status Panel */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Approval Status</h2>
              
              {/* Partner Approval Status */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Partner Approval</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    application.partner_approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                    application.partner_approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {application.partner_approval_status === 'approved' ? 'Approved' :
                     application.partner_approval_status === 'rejected' ? 'Rejected' :
                     'Pending'}
                  </span>
                </div>
                {application.partner_approval_by_email && (
                  <p className="text-xs text-gray-500">
                    By: {application.partner_approval_by_email}
                    {application.partner_approval_at && ` • ${formatDate(application.partner_approval_at)}`}
                  </p>
                )}
              </div>
              
              {/* Super Admin Approval Status */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Super Admin Approval</span>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                    application.super_admin_approval_status === 'approved' ? 'bg-green-100 text-green-800' :
                    application.super_admin_approval_status === 'rejected' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {application.super_admin_approval_status === 'approved' ? 'Approved' :
                     application.super_admin_approval_status === 'rejected' ? 'Rejected' :
                     'Pending'}
                  </span>
                </div>
                {application.super_admin_approval_by_email && (
                  <p className="text-xs text-gray-500">
                    By: {application.super_admin_approval_by_email}
                    {application.super_admin_approval_at && ` • ${formatDate(application.super_admin_approval_at)}`}
                  </p>
                )}
              </div>
              
              {/* Final Status */}
              <div className="p-3 rounded-lg border-2 border-dashed">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-900">Final Status</span>
                  <span className={`text-sm px-3 py-1 rounded-full font-bold ${
                    application.final_approval_status === 'approved' ? 'bg-green-500 text-white' :
                    application.final_approval_status === 'rejected' ? 'bg-red-500 text-white' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {application.final_approval_status === 'approved' ? 'APPROVED' :
                     application.final_approval_status === 'rejected' ? 'REJECTED' :
                     'PENDING'}
                  </span>
                </div>
                {application.final_approval_status === 'pending' && (
                  <p className="text-xs text-gray-500 mt-2">
                    Requires both Partner and Super Admin approval
                  </p>
                )}
              </div>
            </div>

            {/* Quick Actions - Role-Gated */}
            {!FINAL_STATUSES.includes(application.status) && application.final_approval_status !== 'approved' && application.final_approval_status !== 'rejected' && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  {isSuperAdmin ? 'Super Admin Actions' : 'Partner Actions'}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isSuperAdmin ? 'bg-purple-100 text-purple-800' : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {isSuperAdmin ? 'super_admin' : 'ops_admin'}
                  </span>
                </h2>
                
                <div className="space-y-3">
                  {/* Partner (ops_admin) Actions */}
                  {isOpsAdmin && !isSuperAdmin && (
                    <>
                      {application.partner_approval_status === 'pending' ? (
                        <>
                          <button
                            onClick={() => handlePartnerApproval('approve')}
                            disabled={saving}
                            data-testid="partner-approve-btn"
                            className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            Approve (Partner Approval)
                          </button>
                          
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={saving}
                            data-testid="partner-reject-btn"
                            className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                          >
                            <ThumbsDown className="w-4 h-4" />
                            Reject (Partner Rejection)
                          </button>
                        </>
                      ) : (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-blue-700">
                          <p className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Partner approval recorded. Awaiting Super Admin final decision.
                          </p>
                        </div>
                      )}
                      
                      <button
                        onClick={() => setShowEscalateModal(true)}
                        disabled={saving}
                        data-testid="quick-escalate-btn"
                        className="w-full px-4 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                      >
                        <ShieldAlert className="w-4 h-4" />
                        Escalate for Review
                      </button>
                    </>
                  )}
                  
                  {/* Super Admin Actions */}
                  {isSuperAdmin && (
                    <>
                      {/* Show Partner status indicator */}
                      <div className={`p-3 rounded-lg border text-sm ${
                        application.partner_approval_status === 'approved' ? 'bg-green-50 border-green-200 text-green-700' :
                        application.partner_approval_status === 'rejected' ? 'bg-red-50 border-red-200 text-red-700' :
                        'bg-yellow-50 border-yellow-200 text-yellow-700'
                      }`}>
                        <p className="font-medium">
                          Partner: {application.partner_approval_status === 'approved' ? 'Approved' :
                                   application.partner_approval_status === 'rejected' ? 'Rejected' :
                                   'Pending'}
                        </p>
                        {application.partner_approval_by_email && (
                          <p className="text-xs mt-1">by {application.partner_approval_by_email}</p>
                        )}
                      </div>
                      
                      {application.super_admin_approval_status === 'pending' && application.final_approval_status !== 'rejected' ? (
                        <>
                          <button
                            onClick={() => handleSuperAdminApproval('approve')}
                            disabled={saving || application.partner_approval_status === 'rejected'}
                            data-testid="super-admin-approve-btn"
                            className="w-full px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                          >
                            <ThumbsUp className="w-4 h-4" />
                            Approve (Final Approval)
                          </button>
                          
                          {application.partner_approval_status === 'rejected' && (
                            <p className="text-xs text-red-600 text-center">Partner rejected — final is rejected</p>
                          )}
                          
                          <button
                            onClick={() => setShowRejectModal(true)}
                            disabled={saving}
                            data-testid="super-admin-reject-btn"
                            className="w-full px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                          >
                            <ThumbsDown className="w-4 h-4" />
                            Reject (Final Decision)
                          </button>
                        </>
                      ) : (
                        <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm text-purple-700">
                          <p className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            Super Admin decision recorded.
                          </p>
                        </div>
                      )}
                      
                      <button
                        onClick={handleRequestDocs}
                        disabled={saving}
                        data-testid="quick-request-docs-btn"
                        className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                      >
                        <FileQuestion className="w-4 h-4" />
                        Request Documents
                      </button>
                      
                      {/* Reset Approvals Button - Super Admin Only */}
                      <button
                        onClick={() => setShowResetModal(true)}
                        disabled={saving}
                        data-testid="reset-approvals-btn"
                        className="w-full px-4 py-2.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Reset Approvals
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            
            {/* Inline Audit Snippet (Last 5 Events) */}
            {auditEvents.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
                <div className="space-y-2">
                  {auditEvents.map((event, index) => (
                    <div key={index} className="p-2 bg-gray-50 rounded-lg text-xs">
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${
                          event.action?.includes('APPROVED') ? 'text-green-700' :
                          event.action?.includes('REJECTED') ? 'text-red-700' :
                          event.action?.includes('RESET') ? 'text-gray-700' :
                          'text-blue-700'
                        }`}>
                          {event.action?.replace(/_/g, ' ')}
                        </span>
                        <span className="text-gray-400">{formatDate(event.timestamp)}</span>
                      </div>
                      {event.details?.actor_email && (
                        <p className="text-gray-500 mt-1">by {event.details.actor_email}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Status */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
              
              {/* Current status badge */}
              <div className="mb-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  application.status === 'rejected' ? 'bg-red-100 text-red-800' :
                  application.status === 'active_dispatch_eligible' ? 'bg-green-100 text-green-800' :
                  APPROVAL_STATUSES.includes(application.status) ? 'bg-blue-100 text-blue-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {STATUSES.find(s => s.value === application.status)?.label || application.status}
                </span>
              </div>
              
              <select
                value={application.status}
                onChange={(e) => updateStatus(e.target.value)}
                disabled={saving || (!isSuperAdmin && (APPROVAL_STATUSES.includes(application.status) || REJECTION_STATUSES.includes(application.status)))}
                data-testid="status-select"
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  !isSuperAdmin ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
              >
                {STATUSES.map(s => (
                  <option 
                    key={s.value} 
                    value={s.value}
                    disabled={!isSuperAdmin && (APPROVAL_STATUSES.includes(s.value) || REJECTION_STATUSES.includes(s.value))}
                  >
                    {s.label}
                  </option>
                ))}
              </select>
              
              {!isSuperAdmin && (
                <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Status changes restricted for ops_admin
                </p>
              )}
              
              <p className="text-xs text-gray-500 mt-2">
                Last updated: {formatDate(application.updated_at)}
              </p>
            </div>

            {/* Onboarding Link */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Onboarding Link</h2>
              
              {application.onboarding_token ? (
                <>
                  {/* Token Status Badge */}
                  <div className="mb-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      application.token_status === 'active' ? 'bg-green-100 text-green-800' :
                      application.token_status === 'used' ? 'bg-blue-100 text-blue-800' :
                      application.token_status === 'expired' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {application.token_status === 'active' ? 'Active' :
                       application.token_status === 'used' ? 'Used' :
                       application.token_status === 'expired' ? 'Expired' : 'Unknown'}
                    </span>
                  </div>

                  {/* Link */}
                  <div className="bg-gray-50 rounded-lg p-3 break-all text-sm text-gray-600 mb-3">
                    {`${window.location.origin}/driver-onboarding/${application.onboarding_token}`}
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/driver-onboarding/${application.onboarding_token}`)}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 mb-3"
                  >
                    <Copy className="w-4 h-4" />
                    Copy Link
                  </button>

                  {/* Token Audit Info */}
                  <div className="space-y-1 text-xs text-gray-500 border-t border-gray-100 pt-3">
                    {application.token_generated_at && (
                      <p><span className="font-medium">Created:</span> {formatDate(application.token_generated_at)}</p>
                    )}
                    {application.token_generated_by && (
                      <p><span className="font-medium">Created by:</span> {application.token_generated_by}</p>
                    )}
                    {application.token_expires_at && (
                      <p><span className="font-medium">Expires:</span> {formatDate(application.token_expires_at)}</p>
                    )}
                    {application.docs_submitted_at && (
                      <p><span className="font-medium">Submitted:</span> {formatDate(application.docs_submitted_at)}</p>
                    )}
                  </div>

                  {/* Generate New Token Button (always available) */}
                  {(application.token_status === 'used' || application.token_status === 'expired') && (
                    <button
                      onClick={generateToken}
                      disabled={generatingToken}
                      className="w-full mt-3 px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <LinkIcon className="w-4 h-4" />
                      {generatingToken ? 'Generating...' : 'Generate New Link'}
                    </button>
                  )}
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-500 mb-3">No onboarding link generated yet.</p>
                  <button
                    onClick={generateToken}
                    disabled={generatingToken}
                    className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
                  >
                    <LinkIcon className="w-4 h-4" />
                    {generatingToken ? 'Generating...' : 'Generate Onboarding Link'}
                  </button>
                </div>
              )}
            </div>

            {/* Quick Navigation - Cross-linking */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h2>
              <div className="space-y-2">
                {/* Open the unified Person File for this exact record */}
                <Link
                  to={`/admin/person/${application.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid="nav-to-applicant"
                >
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-gray-700">Open Person File</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </Link>

                {/* If driver is active, link to compliance */}
                {application.status === 'active_dispatch_eligible' && (
                  <Link
                    to={`/admin/compliance?driver=${application.id}`}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    data-testid="nav-to-compliance"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-gray-700">Compliance Checklist</span>
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-400" />
                  </Link>
                )}

                {/* Audit Log link for this application */}
                <Link
                  to={`/admin/audit-logs?entity_type=application&search=${application.id}`}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  data-testid="nav-to-audit"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-600" />
                    <span className="text-sm text-gray-700">View Change History</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Message Template Modal */}
        {showMessageModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Message Template</h3>
                <button onClick={() => setShowMessageModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto">
                <pre className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700 whitespace-pre-wrap font-sans">
                  {messageTemplate}
                </pre>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Close
                </button>
                <button
                  onClick={() => { copyToClipboard(messageTemplate); setShowMessageModal(false); }}
                  className="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Message
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Escalation Modal */}
        {showEscalateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-orange-500" />
                  Escalate to Super Admin
                </h3>
                <button onClick={() => setShowEscalateModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  This will flag the application for Super Admin review. Please provide a detailed reason for escalation.
                </p>
                <textarea
                  value={escalationReason}
                  onChange={(e) => setEscalationReason(e.target.value)}
                  placeholder="Reason for escalation (minimum 10 characters)..."
                  rows={4}
                  data-testid="escalation-reason-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {escalationReason.length}/10 characters minimum
                </p>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEscalateModal(false);
                    setEscalationReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={escalating}
                >
                  Cancel
                </button>
                <button
                  onClick={handleEscalate}
                  disabled={escalating || escalationReason.length < 10}
                  data-testid="confirm-escalate-btn"
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2"
                >
                  <ShieldAlert className="w-4 h-4" />
                  {escalating ? 'Escalating...' : 'Escalate'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Reject Modal - Dual Approval */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ThumbsDown className="w-5 h-5 text-red-500" />
                  {isSuperAdmin ? 'Reject (Final Decision)' : 'Reject (Partner Rejection)'}
                </h3>
                <button onClick={() => setShowRejectModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  {isSuperAdmin 
                    ? 'As Super Admin, this rejection will finalize the application outcome.'
                    : 'As Partner, this rejection will finalize the application (either party can reject).'}
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Rejection reason..."
                  rows={4}
                  data-testid="reject-reason-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={() => isSuperAdmin ? handleSuperAdminApproval('reject') : handlePartnerApproval('reject')}
                  disabled={saving || !rejectReason.trim()}
                  data-testid="confirm-reject-btn"
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <ThumbsDown className="w-4 h-4" />
                  {saving ? 'Rejecting...' : (isSuperAdmin ? 'Reject (Final)' : 'Reject (Partner)')}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Reset Approvals Modal - Super Admin Only */}
        {showResetModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-gray-500" />
                  Reset Approvals
                </h3>
                <button onClick={() => setShowResetModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  This will reset all approval statuses to PENDING. Both Partner and Super Admin will need to re-approve.
                </p>
                <p className="text-sm text-amber-600 mb-4 p-2 bg-amber-50 rounded flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" /> This action is logged and auditable.
                </p>
                <textarea
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="Reason for resetting approvals..."
                  rows={3}
                  data-testid="reset-reason-input"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {resetReason.length}/5 characters minimum
                </p>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setResetReason('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetApprovals}
                  disabled={saving || resetReason.length < 5}
                  data-testid="confirm-reset-btn"
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  {saving ? 'Resetting...' : 'Reset Approvals'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OnboardingDetails;
